import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {MdMic, MdMicNone, MdMicOff} from 'react-icons/md';
import {Offcanvas, OffcanvasBody} from 'reactstrap';
import {ImSpinner2} from 'react-icons/im';
import {toast} from 'react-toastify';
import ProblemDetailsError from '../api/ProblemDetailsError';
import IconButton from '../components/ui/IconButton';
import {
    hasExtensionCapability,
    isEmbeddedInExtension,
    postMicrophonePermissionRequest,
    postVoiceSessionState
} from '../lib/extensionEmbed';
import {
    getRealtimeVoiceLabel,
    isRealtimeVoice,
    realtimeVoices
} from './realtimeVoices';
import {
    VoiceAnnouncement,
    VoiceToolDefinition,
    VoiceSessionResponse,
    VoicePageContext,
    useVoiceAction,
    useVoiceControlSurface
} from './VoiceControlSurfaceContext';
import {sliceSpokenText} from './voiceMessageText';
import {
    MicrophoneFailure,
    classifyMicrophoneFailure,
    getMicrophoneFailureMessage
} from './microphoneAccessError';
import {waitForUiSurfaceToSettle} from './voiceUiTiming';
import {
    VoiceActivityState,
    voiceActivityIndicatorDelayMs
} from './voiceActivity';
import VoicePanelContent, {VoicePanelContentProps} from './VoicePanelContent';
import VoiceSettingsModal from './VoiceSettingsModal';
import {useRealtimeVoicePreference} from './useRealtimeVoicePreference';
import useVoiceAssistantAvailability from './useVoiceAssistantAvailability';
import {useTeamsContext} from '../context/TeamsContext';
import {useVoiceAutoReadPreference} from './useVoiceAutoReadPreference';
import {
    downloadVoiceTranscript,
    VoiceTranscriptFormat,
    VoiceTranscriptLine
} from './voiceTranscriptExport';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error';
type VoiceProtocol = VoiceSessionResponse['protocol'];

interface ErrorDetails {
    stage: string;
    name?: string;
    message: string;
    stack?: string;
    raw: unknown;
    microphoneFailure?: MicrophoneFailure;
}

interface VoiceAssistantControlProps {
    id: string;
    className?: string;
    tooltipPlacement?: 'bottom-end' | 'left';
    /**
     * hover: desktop popover on hover or focus, click connects or mutes.
     * sheet: touch layout; click opens a persistent bottom sheet that holds
     * the controls, and nothing connects until the sheet's button is tapped.
     */
    presentation?: 'hover' | 'sheet';
}

const otherSurfacesSummary =
    "Elsewhere in Theta Assist: Home lists the user's own assistants (search, favourites filter, read and open assistants); Library lists all assistants (search, filters, read results, open or add to Home); Threads lists conversations (search, filters, read titles, find or open a thread); an open assistant supports drafting, sending, renaming, pinning, favouriting, starting a new thread, deleting the thread, reading the conversation back, stopping or recovering a response, follow-on operations, and MCP approvals. When the help widget is enabled, product how-to questions can be posed to it. The voice widget itself can report or change its voice, download the transcript, copy error details, and end the session. Use navigate_known_route to reach Home, Library, or Threads.";

// Only when the SPA runs inside the Chrome extension side panel, where the
// browser tools (voice/VoiceBrowserActions) are registered.
const embeddedInExtension = isEmbeddedInExtension();

const embeddedSurfacesSummary =
    'The panel sits beside a web page in the browser. Browser tools can read, search, and scroll that page, open web addresses, go back or forward, list or switch tabs, take a screenshot, and attach the page to the message box.';

const embeddedRoutingInstructions = [
    'You are running inside the browser side panel, so two different things can be read here. Tools with browser in the name act on the web page open in the browser tab. Thread, draft, Home, Library, and Threads tools act on the Theta Assist panel.',
    '"Read the page", "what is on this page", "summarise this page", "what page am I on", "find X on the page", "scroll down", "go back", and "what tabs are open" refer to the browser tab. "Read the reply" and "what did it say" refer to the open thread.',
    '"Go to" followed by a website, domain, or web address means navigate_browser. "Go to Home, Library, or Threads" means navigate_known_route.',
    'For read_browser_page, use part summary to answer questions about the page in your own words, and part full only when the user asks to hear the page read out. When a result is truncated, offer to continue and pass its nextOffset as offset.',
    '"Take a screenshot" and "attach this page" add the page to the message box without sending it. Tell the user it is attached; they can then say send, or dictate a question and send. If those tools are not available, an assistant must be opened first.',
    'If a browser tool says the extension needs updating, did not answer, or cannot read this tab, say so briefly and do not retry.',
    'The sentence beginning "The browser tab shows" is authoritative about which web page is open beside the panel.',
    "Prefer the open assistant over browser tools for data that lives in a connected service such as email, calendar, files, or a business system; use browser tools for the web page beside the panel. Example: on a timesheet page the user asks for this week's meetings. Ask the assistant, read the meetings back, then offer to fill the page with act_on_browser_page.",
    'You cannot see the page. snapshot_browser_page lists its elements; look_at_browser_page has a vision model describe a screenshot and answer one question about it. Look only when the snapshot cannot answer: the user describes something by appearance or position (an icon, a colour, "the pencil next to Monday", "top right"), asks about a picture, chart, or layout on the page, the snapshot is ambiguous or shows icon-only controls, or an action could not find its target. Ask a specific question, pass region_ref for the row, panel, or dialog you mean when you know it, then take a fresh snapshot before acting on what it tells you. Do not look on every step. A look returns a short answer to speak plus the screen set out line by line, such as each field with its current value: answer follow-up questions from that detail rather than looking again, and do not read the whole list aloud unless the user asks for it.',
    'Working on a page is silent for the user, so keep them with you. Say what you are about to do before the first browser action, name each key step as you reach it, such as "opening the timesheet tab" or "filling in Monday now", and say when the page is done. Announce a look before you call look_at_browser_page, and afterwards say what you saw. Do not announce every snapshot, scroll, or read.',
    'Looking is for orientation, not for work. Anything that needs the page analysed, judged, or produced, such as redesigning it, checking it against a brand or a spec, summarising a long document, or anything the user wants kept in the conversation, goes to the open assistant: capture_browser_screenshot or attach_browser_page_content, then send_current_thread_message with the request.'
];

const buildVoiceActionInstructions = (context: VoicePageContext) => {
    const capabilities = context.capabilities ?? [];
    // Tool definitions already carry parameters and descriptions, so the
    // instructions list only ids and labels to keep every session.update small.
    const capabilityList =
        capabilities.length > 0
            ? capabilities
                  .map(capability => `${capability.id} (${capability.label})`)
                  .join('; ')
            : 'none';
    return [
        'You are the Theta Assist voice companion.',
        'The available function tools are authoritative: these are the UI actions you can perform right now.',
        'For UI requests, call the matching function tool directly with its documented parameters instead of explaining manual steps.',
        'Tool names are the exact UI action ids. Never invent tool names, and never wrap arguments in actionId, id, input, or args unless that wrapper is explicitly part of the tool schema.',
        'The line beginning "Where the user is" is authoritative about the current page. When it says an assistant is open, requests to ask, tell, send, reply, or message go to that assistant through send_current_thread_message or draft_current_thread_message; never ask which assistant the user means. Only ask when no assistant is open, and then offer open_assistant_by_name.',
        'The open assistant is your helper. The sentence beginning "It can" after its name lists what it can do, such as reading a connected calendar or mailbox, searching the web, searching its knowledge files, or producing files. When the user asks for information or work those capabilities cover, and the request is not about this panel, say in one short sentence that you are asking the assistant, then call send_current_thread_message and answer from its result. If the result says it is still working, wait for the UI event instead of answering from memory. For detail on what it can do, call describe_current_assistant; never guess its capabilities.',
        'UI events also report progress such as "The assistant is searching the web", a finished run that may list files or images, or a pending approval. Speak progress in one short sentence. When an event lists files or images, offer to describe them with read_current_thread_context part outputs. When it says approval is pending, tell the user which tool and service, ask whether to allow it, and call approve_pending_mcp_call or deny_pending_mcp_call.',
        'While the assistant is still responding, say only that you are still waiting for its reply. Never say it is not working, not showing anything, or has failed unless a tool result or UI event says the run failed, and never send the same request again while it is running. A result whose state is running means exactly that: the reply is on its way.',
        'When the user uses view-filter language such as mine, shared, shared with me, shared by me, certified, pinned, or favourites/favorites, prefer a filter/toggle tool over a text search tool. For a direct thread identification or recency question, pass that scope to the thread resolver. Only put those words in a search box when the user clearly asks for literal text search.',
        'Favourite, pin, and favourites-filter tools take an explicit target state. Pass the state the user asked for, such as favourite true for "favourite this" or pinned false for "unpin", and omit it only when the user explicitly asks to toggle. If the result says the state already held, tell the user nothing changed.',
        'Message posting policy for an open assistant thread: if send_current_thread_message and draft_current_thread_message are available, route message-like requests by confidence.',
        'Clear intent means send immediately with send_current_thread_message. Clear send phrases include ask it, ask the current assistant, tell it, tell the assistant, send, reply, message the assistant, follow up with, or explicit wording like "Reply: ...", "Send: ...", and "Ask it to ...".',
        'Likely composing intent means draft with draft_current_thread_message. Draft when the user sounds exploratory or asks to compose wording, for example "maybe say ...", "I want to ask about ...", "can you write something like ...", or "put together a question about ...".',
        'Ambiguous message-like utterances should not be sent or drafted. Ask one short clarification, such as "Do you want me to send that to the current assistant, or just draft it?" Ambiguous examples include "What about the budget?", "This is wrong", "Can we do that?", or "Summarize this".',
        'To open or switch to an assistant from anywhere, including from inside another assistant, use open_assistant_by_name directly; never navigate to Home first. On Library prefer access_library_assistant_by_name so the active filter applies. If the user did not provide an assistant name, ask which assistant they want.',
        'To say what an assistant can do without opening it, from Home, Library, or anywhere else, use describe_assistant_by_name with its name, or with the assistantId from a read-back item when the user picks one by number. Use it before open_assistant_by_name when the user is deciding which assistant to use.',
        'When a tool result says there are suggestions or ambiguous matches and includes candidates, ask which candidate they mean. If the user answers with an ordinal, assistant name, thread date, or other clue from that candidate list, call the same tool again using the candidate id fields from the prior tool result when the tool schema allows it.',
        'When use_current_follow_on_operation is available, call it without an operation to answer which follow-on operations or options are available. Call it with the selected operation when the user asks to run, use, try, or choose one of those options.',
        'When a read tool such as read_visible_thread_titles, read_home_assistants, or read_library_assistants is available, use it for requests to list, read, count, or describe what is shown on the page, and pass page for "next page" or "page two". When the user then picks an item by number or name, call the matching open or find tool with that item\'s ids from the read result rather than searching again.',
        'For thread requests, distinguish identification from navigation. Use the thread resolver with open false for questions such as "what was my latest thread?" or "which thread was about X?" Set open true only when the user explicitly asks to open, access, or go to that thread. Map latest, most recent, and newest to latest; map oldest and earliest to oldest.',
        'When read_current_thread_context is available, use it for anything about the open conversation: what the assistant said, what the user asked, an earlier message by position, the current draft, the thread name, assistant, model, or whether a response is still running. Do not describe the conversation from memory. When a result is truncated, offer to continue and pass its nextOffset as offset.',
        'Use stop_current_run when the user says stop, cancel, or that is enough while a response is running. When read_current_failure or recover_failed_turn is available, the last message failed: explain the failure and the available options before acting, and act only when the user chooses one.',
        'Destructive tools such as delete_current_thread and the delete action of recover_failed_turn ask a confirmation question on the first call and do nothing. Put that question to the user, and call the tool again with confirm true only after they clearly say yes in reply. If they decline or say anything else, do not call it again.',
        'Use start_new_thread for "new thread", "new conversation", or "start over" with the current assistant. Set send true only when the user clearly asked to send the first message immediately.',
        'Use read_selected_text when the user refers to text they have selected or highlighted on the page, then answer from the returned text.',
        'Messages beginning with "[UI event]" come from the page, not the user, and describe something that just changed, such as a reply arriving, the assistant starting a tool, a run finishing, or a run failing. When the user asks to read a reply, call read_current_thread_context at once. Never say you will wait for a reply to arrive: if a UI event says the reply is ready or the run finished, it is already on screen. When a UI event asks you to read a reply aloud, speak the supplied text word for word with no preamble, then stop.',
        'Draft editing: when the user asks to shorten, expand, fix typos, tidy, change the tone, or otherwise rework what is in the message box, first call read_current_draft (readAloud false) and work only from the text it returns, never from memory or from earlier replies; rewrite that text keeping the meaning and any facts the user wrote, then call edit_current_draft with mode replace, the complete new text, and the draftKey from the read result. Afterwards say only the confirmation sentence the tool returns; never recite or invent draft content. If the box is empty, say so. Use append or prepend to add to a draft, clear to empty it, and read_current_draft with readAloud true when the user wants to hear it.',
        'Use set_auto_read when the user asks to have replies read out automatically ("read replies out loud from now on") or to stop that ("stop reading replies automatically"). get_voice_session_info reports whether auto-read is on.',
        'When ask_product_help is available, use it for "how do I", "where is", or "what does this do" questions about using this application itself, such as how to share a thread, what a feature does, or where a setting lives. Users may call the product by any name. Those are not messages for the current assistant. If it is unclear whether the user means the application or the assistant, ask one short clarification first.',
        'For the voice widget itself, use get_voice_session_info for "which voice are you using", set_voice to change the voice for the next connection, download_voice_transcript for transcript downloads, and copy_voice_error_details to copy the error report.',
        'Ending the chat: "end chat", "end the chat", "close the voice chat", "disconnect", "stop listening", "hang up", or a goodbye with nothing pending are clear, so call end_voice_session at once without asking. "Stop" while a reply is running means stop_current_run, not ending the chat. Wording that could mean either, such as "I am done", "that is enough", "we are finished", or a bare "stop" when nothing is running, gets one short question, "Do you want me to end the voice chat?", and end_voice_session is called only after a clear yes.',
        'Treat tool results with status "loading" as temporary. Explain what information the page is still loading, say that you are waiting for it to finish, and ask whether the user wants you to wait and retry or identify the item another way. Do not describe loading information as missing, empty, or unavailable. Only retry with a tool wait/retry parameter after the user agrees to wait.',
        ...(embeddedInExtension ? embeddedRoutingInstructions : []),
        `Say what you are doing before any tool call that takes more than a moment, such as ${
            embeddedInExtension
                ? 'looking at the page, acting on the page, opening a web address, reading a long page, or asking the assistant'
                : 'asking the assistant, opening an assistant by name, or asking the help widget'
        }. One short line first, for example ${
            embeddedInExtension
                ? '"Let me take a look at the page"'
                : '"Let me check that"'
        } or "Asking the assistant now", then make the call. You cannot speak while a tool is running, so without that line the user hears silence and cannot tell anything is happening.`,
        'When a request takes several steps, narrate the key points and not every call: what you are starting, when you move to a different kind of step, anything unexpected such as a retry or something you could not find, and when it is done. Aim for a short line every few seconds of work, not one per tool call.',
        `Quick lookups need no announcement: ${
            embeddedInExtension ? 'page info, tabs, ' : ''
        }the current draft, session settings, and reading the conversation back.`,
        'If the user asks what they can do, summarize the available tool labels on this page and mention what other pages offer, without invoking an action.',
        'Keep spoken responses concise.',
        '',
        otherSurfacesSummary,
        ...(embeddedInExtension ? [embeddedSurfacesSummary] : []),
        `Where the user is: ${context.description}`,
        `Available actions on this page: ${capabilityList}.`
    ].join('\n');
};

const buildVoiceTools = (context: VoicePageContext): VoiceToolDefinition[] =>
    (context.capabilities ?? []).map(capability => ({
        type: 'function',
        name: capability.id,
        description: `${capability.label}. ${capability.description}`,
        parameters: capability.parameters
    }));

const parseEvent = (event: MessageEvent) => JSON.parse(event.data);

const emptyParameters = {
    type: 'object',
    properties: {},
    additionalProperties: false
};

// Long enough for the spoken acknowledgement to finish before the channel
// closes.
const endSessionDelayMs = 3_000;

// Shown while muted so nobody mistakes mute for ending the session. The copy
// avoids billing wording, but the provider still bills the open session until
// it is disconnected or the idle timeout closes it.
const mutedStatus =
    'Microphone muted. The session stays open until you disconnect or it times out.';

// Idle handling. The provider bills every second the session is open, silence
// included, so silence closes the session after the admin-configured limit.
// The check-in is spoken this long before the close so the user can keep it
// alive by answering. While the open thread is generating a reply the clock
// is held, capped so a stuck run cannot keep a session open forever.
const idleCheckInLeadMs = 15_000;
const runHoldCapMs = 5 * 60_000;
const idleTickMs = 1_000;

// Replies read aloud automatically are chunked like read_current_thread_context.
const autoReadChunkCharacters = 1_500;

const getCurrentAssistantId = () =>
    window.location.pathname.match(/\/assistants\/([^/]+)/)?.[1];

// A short falling two-tone chime so the user hears that the session closed
// even when they are not looking at the screen. Synthesised so there is no
// audio asset to ship.
const playIdleCloseTone = () => {
    try {
        const audioContext = new AudioContext();
        const start = audioContext.currentTime;
        [
            [660, 0],
            [440, 0.2]
        ].forEach(([frequency, offset]) => {
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();
            oscillator.type = 'sine';
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, start + offset);
            gain.gain.exponentialRampToValueAtTime(0.25, start + offset + 0.02);
            gain.gain.exponentialRampToValueAtTime(
                0.0001,
                start + offset + 0.18
            );
            oscillator.connect(gain).connect(audioContext.destination);
            oscillator.start(start + offset);
            oscillator.stop(start + offset + 0.2);
        });
        window.setTimeout(() => {
            void audioContext.close();
        }, 800);
    } catch {
        // No audio output available; the toast and status text still show.
    }
};

const copyTextToClipboard = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
    }
};

const getRealtimeOutputText = (item: {
    content?: Array<{text?: string; transcript?: string}>;
}) =>
    item.content?.map(part => part.text ?? part.transcript ?? '').join('') ??
    '';

const appendCompletedTranscriptLine = (
    lines: VoiceTranscriptLine[],
    role: VoiceTranscriptLine['role'],
    text: string
) => {
    if (!text.trim()) {
        return lines;
    }

    const last = lines[lines.length - 1];
    if (last?.role === role && last.text.trim() === text.trim()) {
        return lines;
    }

    return [...lines, {role, text}];
};

const waitForIceGathering = (connection: RTCPeerConnection) => {
    if (connection.iceGatheringState === 'complete') {
        return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
            connection.removeEventListener(
                'icegatheringstatechange',
                handleStateChange
            );
            reject(new Error('Timed out while gathering ICE candidates.'));
        }, 10_000);

        const handleStateChange = () => {
            if (connection.iceGatheringState !== 'complete') {
                return;
            }

            window.clearTimeout(timeout);
            connection.removeEventListener(
                'icegatheringstatechange',
                handleStateChange
            );
            resolve();
        };

        connection.addEventListener(
            'icegatheringstatechange',
            handleStateChange
        );
        handleStateChange();
    });
};

// Failures after the microphone was granted: WebRTC setup and the session
// request. Microphone refusals are worded by microphoneAccessError.
const getConnectErrorMessage = (error: unknown) => {
    if (error instanceof ProblemDetailsError) {
        const serverMessages = (error.problemDetails.errors ?? [])
            .map(serviceError => serviceError.message)
            .filter((message): message is string => Boolean(message?.trim()));
        if (serverMessages.length > 0) {
            return serverMessages.join(' ');
        }

        return (
            error.problemDetails.detail ??
            error.problemDetails.title ??
            'Unable to create voice session.'
        );
    }

    const message = error instanceof Error ? error.message : String(error);
    return message || 'Unable to start the voice session.';
};

const isMicrophonePermissionError = (error: unknown): boolean =>
    error instanceof DOMException &&
    (error.name === 'NotAllowedError' ||
        error.name === 'PermissionDeniedError');

// Inside the Chrome extension side panel, Chrome never shows the microphone
// prompt: getUserMedia fails at once with "Permission dismissed". The
// extension grants the permission from a full tab instead, and this iframe
// then inherits it through allow="microphone". True when that grant is still
// missing, so connect should hand over to the extension rather than prompt.
const isMicrophonePermissionMissing = async (): Promise<boolean> => {
    if (typeof navigator.permissions?.query !== 'function') {
        return false;
    }
    try {
        const status = await navigator.permissions.query({
            name: 'microphone' as PermissionName
        });
        return status.state !== 'granted';
    } catch {
        return false;
    }
};

const microphonePermissionCapability = 'microphone_permission';

// Teams keeps its own per-app device consent, so a refusal inside the Teams
// tab is undone there rather than in the browser's site settings.
const teamsMicrophoneBlockedMessage =
    'Microphone access was blocked. In Teams, open Settings > App permissions, select this app, and allow Media.';

const embeddedMicrophoneHelpMessage =
    'Microphone access is needed. A tab has opened where you can allow it for the Theta Assist extension. Once allowed, tap the microphone here to connect.';

const embeddedMicrophoneUpdateMessage =
    'Chrome does not allow the microphone prompt inside the side panel. Update the Theta Assist extension, then use its Allow microphone option.';

const createErrorDetails = (stage: string, error: unknown): ErrorDetails => {
    if (error instanceof ProblemDetailsError) {
        return {
            stage,
            name: error.name,
            message: getConnectErrorMessage(error),
            stack: error.stack,
            raw: error.problemDetails
        };
    }

    if (error instanceof Error) {
        return {
            stage,
            name: error.name,
            message: error.message,
            stack: error.stack,
            raw: String(error)
        };
    }

    return {
        stage,
        message: String(error),
        raw: JSON.stringify(error, null, 2) ?? String(error)
    };
};

const createResponseErrorDetails = (
    stage: string,
    error: string
): ErrorDetails => ({
    stage,
    message: error,
    raw: error
});

const tooltipGapPx = 12;
const tooltipViewportMarginPx = 12;
const tooltipHideDelayMs = 300;

const VoiceAssistantControl: React.FC<VoiceAssistantControlProps> = ({
    id,
    className,
    tooltipPlacement = 'bottom-end',
    presentation = 'hover'
}) => {
    const isSheet = presentation === 'sheet';
    const {
        actionVersion,
        getPageContext,
        createVoiceSession,
        invokeAction,
        getActionActivity,
        subscribeToAnnouncements,
        trackVoiceEvent
    } = useVoiceControlSurface();
    const {selectedVoice, setSelectedVoice} = useRealtimeVoicePreference();
    const {autoReadEnabled, setAutoReadEnabled} = useVoiceAutoReadPreference();
    const {idleTimeoutSeconds, providerNote} = useVoiceAssistantAvailability();
    const {isRunningInTeams} = useTeamsContext();

    const [connectionState, setConnectionState] =
        useState<ConnectionState>('idle');
    // Seconds until the idle close, shown only during the check-in window.
    const [idleSecondsRemaining, setIdleSecondsRemaining] = useState<
        number | null
    >(null);
    const [status, setStatus] = useState('Voice assistant idle.');
    // What the widget is doing right now on the model's behalf. A tool call is
    // silent (the model cannot speak until it returns), so without this a look
    // at the page or a message to the assistant shows nothing at all.
    const [activity, setActivity] = useState<VoiceActivityState | null>(null);
    const activityTimeoutRef = useRef<number | null>(null);
    const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
    const [errorCopied, setErrorCopied] = useState(false);
    const [conversation, setConversation] = useState<VoiceTranscriptLine[]>([]);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(false);
    const [isAudioPlaybackBlocked, setIsAudioPlaybackBlocked] = useState(false);
    const [leftTooltipTop, setLeftTooltipTop] = useState<number | null>(null);
    const [isVoiceSettingsOpen, setIsVoiceSettingsOpen] = useState(false);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const controlRef = useRef<HTMLDivElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const tooltipHideTimeoutRef = useRef<number | null>(null);
    const copyFeedbackTimeoutRef = useRef<number | null>(null);
    const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
    const dataChannelRef = useRef<RTCDataChannel | null>(null);
    const protocolRef = useRef<VoiceProtocol | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
    const isMicrophoneMutedRef = useRef(false);
    // Idle clock and usage tracking state, all refs so the 1 s tick and the
    // event handlers never need to be re-created.
    const idleTimeoutSecondsRef = useRef(idleTimeoutSeconds);
    idleTimeoutSecondsRef.current = idleTimeoutSeconds;
    const lastActivityAtRef = useRef(Date.now());
    const runHoldStartedAtRef = useRef<number | null>(null);
    const idleCheckInSentRef = useRef(false);
    const connectedAtRef = useRef<number | null>(null);
    const minutesTrackedRef = useRef(0);
    // Azure Realtime rejects response.create while a response is active, so
    // announcements wait for response.done. GPT-Live queues them itself.
    const realtimeResponseActiveRef = useRef(false);
    // GPT-Live equivalent, tracked from the delegated Responses lifecycle events
    // forwarded as response.event. Stays false if the provider never forwards
    // them, which leaves delivery immediate as before.
    const liveResponseActiveRef = useRef(false);
    // Name of the UI tool the model is waiting on, from the function call until
    // its output and the follow-up response.create have been sent. Announcements
    // arriving meanwhile are queued, or dropped when the tool result itself
    // reports the same thing (send_current_thread_message waits for the reply).
    const voiceToolCallInFlightRef = useRef<string | null>(null);
    const pendingAnnouncementsRef = useRef<VoiceAnnouncement[]>([]);
    const deliveredAnnouncementKeysRef = useRef<Set<string>>(new Set());
    // Latest deliverAnnouncement, for the realtime response.done handler.
    const deliverAnnouncementRef = useRef<
        (announcement: VoiceAnnouncement) => void
    >(() => {});

    // User speech, tool calls, and page announcements all count as activity
    // and cancel a pending check-in.
    const markActivity = useCallback(() => {
        lastActivityAtRef.current = Date.now();
        idleCheckInSentRef.current = false;
        setIdleSecondsRemaining(null);
    }, []);

    // Assistant speech keeps the session alive too (a long read-out must not
    // be cut off), but not while the check-in itself is being spoken.
    const touchActivity = useCallback(() => {
        if (!idleCheckInSentRef.current) {
            lastActivityAtRef.current = Date.now();
        }
    }, []);

    const sendVoiceEvent = useCallback((event: Record<string, unknown>) => {
        const channel = dataChannelRef.current;
        if (!channel || channel.readyState !== 'open') {
            return false;
        }

        channel.send(JSON.stringify(event));
        return true;
    }, []);

    const updateLiveContext = useCallback(
        (context: VoicePageContext) => {
            sendVoiceEvent({
                type: 'session.update',
                session: {
                    delegation: {
                        type: 'responses',
                        responses: {
                            instructions: buildVoiceActionInstructions(context),
                            tools: buildVoiceTools(context),
                            tool_choice: 'auto',
                            parallel_tool_calls: false
                        }
                    }
                }
            });
        },
        [sendVoiceEvent]
    );

    const updateAzureRealtimeContext = useCallback(
        (context: VoicePageContext) => {
            sendVoiceEvent({
                type: 'session.update',
                session: {
                    type: 'realtime',
                    instructions: buildVoiceActionInstructions(context),
                    tools: buildVoiceTools(context),
                    tool_choice: 'auto'
                }
            });
        },
        [sendVoiceEvent]
    );

    const setMicrophoneMuted = useCallback(
        (muted: boolean) => {
            mediaStreamRef.current?.getAudioTracks().forEach(track => {
                track.enabled = !muted;
            });
            if (protocolRef.current === 'live') {
                sendVoiceEvent({
                    type: muted
                        ? 'session.input_audio.mute'
                        : 'session.input_audio.unmute'
                });
            }
            isMicrophoneMutedRef.current = muted;
            setIsMicrophoneMuted(muted);
            setStatus(muted ? 'Voice assistant muted.' : 'Listening.');
        },
        [sendVoiceEvent]
    );

    const disconnect = useCallback(
        (
            requestSessionClose = true,
            closeReason: 'ended' | 'idle_closed' = 'ended'
        ) => {
            const dataChannel = dataChannelRef.current;
            const peerConnection = peerConnectionRef.current;
            if (
                requestSessionClose &&
                protocolRef.current === 'live' &&
                dataChannel?.readyState === 'open'
            ) {
                dataChannel.send(JSON.stringify({type: 'session.close'}));
            }
            // A protocol is only set once the server created a provider session,
            // so this records exactly the sessions that were billed.
            if (protocolRef.current !== null) {
                trackVoiceEvent(closeReason);
            }
            connectedAtRef.current = null;
            minutesTrackedRef.current = 0;
            runHoldStartedAtRef.current = null;
            idleCheckInSentRef.current = false;
            realtimeResponseActiveRef.current = false;
            liveResponseActiveRef.current = false;
            voiceToolCallInFlightRef.current = null;
            pendingAnnouncementsRef.current = [];
            deliveredAnnouncementKeysRef.current.clear();
            setIdleSecondsRemaining(null);
            // Clear these first so the channel's close callback recognises an
            // intentional disconnect rather than reporting a connection failure.
            dataChannelRef.current = null;
            peerConnectionRef.current = null;
            protocolRef.current = null;
            dataChannel?.close();
            peerConnection?.close();
            mediaStreamRef.current?.getTracks().forEach(track => track.stop());
            remoteAudioRef.current?.pause();
            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = null;
            }
            mediaStreamRef.current = null;
            isMicrophoneMutedRef.current = false;
            setIsMicrophoneMuted(false);
            setIsAudioPlaybackBlocked(false);
            setConnectionState('idle');
            setStatus('Voice assistant idle.');
            if (activityTimeoutRef.current !== null) {
                window.clearTimeout(activityTimeoutRef.current);
                activityTimeoutRef.current = null;
            }
            setActivity(null);
        },
        [trackVoiceEvent]
    );

    // Widget state the voice actions read at call time, so their handlers stay
    // identity-stable across transcript re-renders.
    const widgetStateRef = useRef({
        connectionState,
        conversation,
        errorDetails,
        selectedVoice,
        autoReadEnabled
    });
    widgetStateRef.current = {
        connectionState,
        conversation,
        errorDetails,
        selectedVoice,
        autoReadEnabled
    };

    const copyErrorDetails = useCallback(async () => {
        const details = widgetStateRef.current.errorDetails;
        if (!details) {
            return false;
        }

        await copyTextToClipboard(JSON.stringify(details, null, 2));
        setErrorCopied(true);
        if (copyFeedbackTimeoutRef.current !== null) {
            window.clearTimeout(copyFeedbackTimeoutRef.current);
        }
        copyFeedbackTimeoutRef.current = window.setTimeout(() => {
            copyFeedbackTimeoutRef.current = null;
            setErrorCopied(false);
        }, 2_500);
        return true;
    }, []);

    useVoiceAction(
        useMemo(
            () => ({
                id: 'get_voice_session_info',
                label: 'Voice Session Info',
                description:
                    'Report which voice is in use, the provider protocol, whether replies are read aloud automatically, the idle timeout, and how many transcript lines there are. Use for "which voice are you using" or "is auto-read on".',
                parameters: emptyParameters
            }),
            []
        ),
        useCallback(() => {
            const state = widgetStateRef.current;
            const voiceLabel = getRealtimeVoiceLabel(state.selectedVoice);
            const protocol = protocolRef.current;
            const idleLimit = idleTimeoutSecondsRef.current;
            const text = `I am using the ${voiceLabel} voice${
                protocol
                    ? ` over ${protocol === 'live' ? 'GPT-Live' : 'Azure Realtime'}`
                    : ''
            }. Auto-read of replies is ${state.autoReadEnabled ? 'on' : 'off'}. ${
                idleLimit > 0
                    ? `The session closes after ${idleLimit} seconds of silence.`
                    : 'The session has no idle timeout.'
            } The transcript has ${state.conversation.length} lines${
                state.errorDetails ? ', and an error report is available' : ''
            }.`;
            return {
                message: text,
                speakText: text,
                data: {
                    voice: state.selectedVoice,
                    voiceLabel,
                    protocol,
                    autoReadEnabled: state.autoReadEnabled,
                    idleTimeoutSeconds: idleLimit,
                    connectionState: state.connectionState,
                    transcriptLines: state.conversation.length,
                    hasError: state.errorDetails !== null
                }
            };
        }, []),
        {global: true}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'set_auto_read',
                label: 'Set Auto-read',
                description:
                    'Turn automatic reading of new replies on or off. When on, a reply that lands in the open thread is read aloud as it arrives; when off, the voice only says the reply is ready. Pass the state the user asked for.',
                parameters: {
                    type: 'object',
                    properties: {
                        enabled: {type: 'boolean'}
                    },
                    required: ['enabled'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(
            args => {
                const enabled = args.enabled === true;
                if (enabled === widgetStateRef.current.autoReadEnabled) {
                    return {
                        message: enabled
                            ? 'Auto-read is already on.'
                            : 'Auto-read is already off.',
                        data: {enabled, changed: false}
                    };
                }

                setAutoReadEnabled(enabled);
                return {
                    message: enabled
                        ? 'Auto-read is on. New replies will be read aloud as they arrive.'
                        : 'Auto-read is off. I will say when a reply is ready and read it on request.',
                    data: {enabled, changed: true}
                };
            },
            [setAutoReadEnabled]
        ),
        {global: true}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'set_voice',
                label: 'Set Voice',
                description:
                    'Choose the voice used for the next voice connection. The change does not affect the current session.',
                parameters: {
                    type: 'object',
                    properties: {
                        voice: {
                            type: 'string',
                            enum: realtimeVoices.map(voice => voice.id)
                        }
                    },
                    required: ['voice'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(
            args => {
                const voice =
                    typeof args.voice === 'string'
                        ? args.voice.trim().toLowerCase()
                        : '';
                if (!isRealtimeVoice(voice)) {
                    return {
                        message: `Unknown voice. Choose from ${realtimeVoices
                            .map(option => option.label)
                            .join(', ')}.`
                    };
                }

                const label = getRealtimeVoiceLabel(voice);
                if (voice === widgetStateRef.current.selectedVoice) {
                    return {
                        message: `${label} is already the selected voice.`,
                        data: {voice, changed: false}
                    };
                }

                setSelectedVoice(voice);
                return {
                    message: `Switched to ${label}. It takes effect the next time you connect.`,
                    data: {voice, changed: true}
                };
            },
            [setSelectedVoice]
        ),
        {global: true}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'download_voice_transcript',
                label: 'Download Voice Transcript',
                description:
                    'Download the transcript of this voice conversation as Markdown or plain text.',
                parameters: {
                    type: 'object',
                    properties: {
                        format: {type: 'string', enum: ['markdown', 'text']}
                    },
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(args => {
            const format: VoiceTranscriptFormat =
                args.format === 'text' ? 'text' : 'markdown';
            const state = widgetStateRef.current;
            downloadVoiceTranscript({
                lines: state.conversation,
                format,
                voiceLabel: getRealtimeVoiceLabel(state.selectedVoice)
            });
            return {
                message: `Downloading the transcript as ${
                    format === 'markdown' ? 'Markdown' : 'plain text'
                }.`,
                data: {format, lines: state.conversation.length}
            };
        }, []),
        {global: true, enabled: conversation.length > 0}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'copy_voice_error_details',
                label: 'Copy Voice Error Details',
                description:
                    'Copy the voice widget error report to the clipboard.',
                parameters: emptyParameters
            }),
            []
        ),
        useCallback(async () => {
            const copied = await copyErrorDetails();
            return {
                message: copied
                    ? 'Copied the error details to the clipboard.'
                    : 'There are no error details to copy.'
            };
        }, [copyErrorDetails]),
        {global: true, enabled: errorDetails !== null}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'end_voice_session',
                label: 'End Chat',
                description:
                    'End the voice chat and close the session. Call it straight away for clear requests such as "end chat", "end the chat", "close the voice chat", "disconnect", "stop listening", "hang up", or "goodbye" with nothing pending. For ambiguous wording such as "I am done", "that is enough", or a bare "stop" when no reply is running, ask "Do you want me to end the voice chat?" first and call this only after a clear yes.',
                parameters: emptyParameters
            }),
            []
        ),
        useCallback(() => {
            const peerConnection = peerConnectionRef.current;
            setStatus('Ending voice session...');
            window.setTimeout(() => {
                if (peerConnectionRef.current === peerConnection) {
                    disconnect();
                }
            }, endSessionDelayMs);
            return {message: 'Ending the chat. Goodbye.'};
        }, [disconnect]),
        {global: true}
    );

    // Shows what the action is doing after a short hold-off and returns the
    // cleanup, so an action that answers instantly never flashes a spinner.
    const showActionActivity = useCallback(
        (actionId: string) => {
            const registered = getActionActivity(actionId);
            if (!registered) {
                return () => {};
            }

            if (activityTimeoutRef.current !== null) {
                window.clearTimeout(activityTimeoutRef.current);
            }
            // Each call owns its own timeout id, so a second action starting
            // before the first returns cannot have its indicator cancelled by
            // the first one's cleanup.
            const timeout = window.setTimeout(() => {
                if (activityTimeoutRef.current === timeout) {
                    activityTimeoutRef.current = null;
                }
                setActivity({actionId, ...registered});
            }, voiceActivityIndicatorDelayMs);
            activityTimeoutRef.current = timeout;

            return () => {
                window.clearTimeout(timeout);
                if (activityTimeoutRef.current === timeout) {
                    activityTimeoutRef.current = null;
                }
                setActivity(current =>
                    current?.actionId === actionId ? null : current
                );
            };
        },
        [getActionActivity]
    );

    const invokeUiAction = useCallback(
        async (actionId: string, args: Record<string, unknown>) => {
            markActivity();
            const hideActivity = showActionActivity(actionId);
            try {
                const result = await invokeAction(actionId, args);
                trackVoiceEvent('action_invoked', getCurrentAssistantId());
                return {ok: true, data: result};
            } catch (error) {
                return {
                    ok: false,
                    error:
                        error instanceof Error
                            ? error.message
                            : 'Unable to invoke voice action.'
                };
            } finally {
                hideActivity();
            }
        },
        [invokeAction, markActivity, showActionActivity, trackVoiceEvent]
    );

    const executeUiToolCall = useCallback(
        async (name: string, argumentsJson: string) => {
            let parsedArgs: Record<string, unknown> = {};
            try {
                parsedArgs = JSON.parse(argumentsJson || '{}');
            } catch {
                parsedArgs = {};
            }

            return name === 'invoke_ui_action'
                ? await invokeUiAction(
                      String(parsedArgs.actionId ?? parsedArgs.id ?? ''),
                      (parsedArgs.args ?? parsedArgs.input ?? {}) as Record<
                          string,
                          unknown
                      >
                  )
                : await invokeUiAction(name, parsedArgs);
        },
        [invokeUiAction]
    );

    const appendTranscriptDelta = useCallback(
        (role: VoiceTranscriptLine['role'], delta: string) => {
            if (!delta) {
                return;
            }

            setConversation(lines => {
                const last = lines[lines.length - 1];
                if (last?.role === role) {
                    return [
                        ...lines.slice(0, -1),
                        {role, text: `${last.text}${delta}`}
                    ];
                }

                return [...lines, {role, text: delta}];
            });
        },
        []
    );

    const handleLiveEvent = useCallback(
        async (event: Record<string, unknown>) => {
            const type = event.type;

            if (type === 'session.input_transcript.delta') {
                markActivity();
                appendTranscriptDelta('user', String(event.delta ?? ''));
                return;
            }

            if (type === 'session.output_transcript.delta') {
                touchActivity();
                appendTranscriptDelta('assistant', String(event.delta ?? ''));
                return;
            }

            if (type === 'session.started') {
                markActivity();
                setErrorDetails(null);
                setConnectionState('connected');
                setStatus('Listening.');
                return;
            }

            if (type === 'session.closed') {
                disconnect(false);
                return;
            }

            if (type === 'error') {
                const liveError = event.error as
                    | {
                          type?: string;
                          code?: string;
                          message?: string;
                          param?: string;
                      }
                    | undefined;
                const message = liveError?.message ?? 'GPT-Live API error.';
                setStatus(message);
                setErrorDetails({
                    stage: 'liveError',
                    name: liveError?.code ?? liveError?.type,
                    message,
                    raw: JSON.stringify(event, null, 2)
                });
                return;
            }

            if (type === 'session.updated') {
                setErrorDetails(null);
                setStatus(
                    isMicrophoneMutedRef.current ? mutedStatus : 'Listening.'
                );
                return;
            }

            if (type === 'session.input_audio.muted') {
                setStatus(mutedStatus);
                return;
            }

            if (type === 'session.input_audio.unmuted') {
                setStatus('Listening.');
                return;
            }

            if (type === 'response.event') {
                const responseEvent = event.event as
                    | Record<string, unknown>
                    | undefined;
                const innerType = responseEvent?.type;
                if (
                    innerType === 'response.created' ||
                    innerType === 'response.in_progress'
                ) {
                    liveResponseActiveRef.current = true;
                } else if (
                    innerType === 'response.completed' ||
                    innerType === 'response.done' ||
                    innerType === 'response.incomplete' ||
                    innerType === 'response.cancelled' ||
                    innerType === 'response.failed'
                ) {
                    liveResponseActiveRef.current = false;
                    if (voiceToolCallInFlightRef.current === null) {
                        const next = pendingAnnouncementsRef.current.shift();
                        if (next) {
                            deliverAnnouncementRef.current(next);
                        }
                    }
                }
                if (responseEvent?.type === 'response.failed') {
                    const response = responseEvent.response as
                        | {
                              error?: {code?: string; message?: string};
                          }
                        | undefined;
                    const message =
                        response?.error?.message ??
                        'The delegated voice request failed.';
                    setStatus(message);
                    setErrorDetails({
                        stage: 'liveDelegation',
                        name: response?.error?.code,
                        message,
                        raw: JSON.stringify(event, null, 2)
                    });
                    return;
                }

                if (responseEvent?.type !== 'response.output_item.done') {
                    return;
                }

                const item = responseEvent.item as
                    | {
                          type?: string;
                          name?: string;
                          call_id?: string;
                          arguments?: string;
                      }
                    | undefined;
                if (item?.type !== 'function_call') {
                    return;
                }

                const name = String(item.name ?? '');
                if (!name) {
                    return;
                }

                const callId = String(item.call_id ?? '');
                voiceToolCallInFlightRef.current = name;
                try {
                    const result = await executeUiToolCall(
                        name,
                        String(item.arguments ?? '{}')
                    );

                    await waitForUiSurfaceToSettle();
                    updateLiveContext(getPageContext());
                    sendVoiceEvent({
                        type: 'response.item.create',
                        item: {
                            type: 'function_call_output',
                            call_id: callId,
                            output: JSON.stringify(result)
                        }
                    });
                    sendVoiceEvent({
                        type: 'response.create'
                    });
                } finally {
                    voiceToolCallInFlightRef.current = null;
                }
            }
        },
        [
            appendTranscriptDelta,
            disconnect,
            executeUiToolCall,
            getPageContext,
            markActivity,
            sendVoiceEvent,
            touchActivity,
            updateLiveContext
        ]
    );

    const handleAzureRealtimeEvent = useCallback(
        async (event: Record<string, unknown>) => {
            const type = event.type;

            if (type === 'input_audio_buffer.speech_started') {
                markActivity();
                return;
            }

            if (type === 'response.created') {
                realtimeResponseActiveRef.current = true;
                return;
            }

            if (type === 'response.done') {
                realtimeResponseActiveRef.current = false;
                if (voiceToolCallInFlightRef.current === null) {
                    const next = pendingAnnouncementsRef.current.shift();
                    if (next) {
                        deliverAnnouncementRef.current(next);
                    }
                }
                return;
            }

            if (type === 'response.audio_transcript.delta') {
                touchActivity();
                return;
            }

            if (
                type === 'conversation.item.input_audio_transcription.completed'
            ) {
                markActivity();
                const transcript = String(event.transcript ?? '');
                if (transcript) {
                    setConversation(lines =>
                        appendCompletedTranscriptLine(lines, 'user', transcript)
                    );
                }
                return;
            }

            if (type === 'response.audio_transcript.done') {
                const transcript = String(event.transcript ?? '');
                if (transcript) {
                    setConversation(lines =>
                        appendCompletedTranscriptLine(
                            lines,
                            'assistant',
                            transcript
                        )
                    );
                }
                return;
            }

            if (type === 'response.output_item.done') {
                const item = event.item as
                    | {
                          type?: string;
                          content?: Array<{
                              text?: string;
                              transcript?: string;
                          }>;
                      }
                    | undefined;
                if (item?.type === 'message') {
                    const text = getRealtimeOutputText(item);
                    if (text) {
                        setConversation(lines =>
                            appendCompletedTranscriptLine(
                                lines,
                                'assistant',
                                text
                            )
                        );
                    }
                }
                return;
            }

            if (type === 'error') {
                const realtimeError = event.error as
                    | {
                          type?: string;
                          code?: string;
                          message?: string;
                      }
                    | undefined;
                const message =
                    realtimeError?.message ?? 'Azure Realtime API error.';
                setStatus(message);
                setErrorDetails({
                    stage: 'azureRealtimeError',
                    name: realtimeError?.code ?? realtimeError?.type,
                    message,
                    raw: JSON.stringify(event, null, 2)
                });
                return;
            }

            if (type === 'session.updated') {
                setErrorDetails(null);
                setStatus(
                    isMicrophoneMutedRef.current ? mutedStatus : 'Listening.'
                );
                return;
            }

            if (type !== 'response.function_call_arguments.done') {
                return;
            }

            const name = String(event.name ?? '');
            if (!name) {
                return;
            }

            voiceToolCallInFlightRef.current = name;
            try {
                const result = await executeUiToolCall(
                    name,
                    String(event.arguments ?? '{}')
                );
                sendVoiceEvent({
                    type: 'conversation.item.create',
                    item: {
                        type: 'function_call_output',
                        call_id: String(event.call_id ?? ''),
                        output: JSON.stringify(result)
                    }
                });

                await waitForUiSurfaceToSettle();
                updateAzureRealtimeContext(getPageContext());
                sendVoiceEvent({type: 'response.create'});
            } finally {
                voiceToolCallInFlightRef.current = null;
            }
        },
        [
            executeUiToolCall,
            getPageContext,
            markActivity,
            sendVoiceEvent,
            touchActivity,
            updateAzureRealtimeContext
        ]
    );

    // Turn a page announcement into provider events. Every announcement gives
    // the delegated model a "[UI event]" context item so it stops assuming it
    // must wait for a reply. Replies are read aloud verbatim (auto-read on) or
    // briefly announced; progress, completion without text, and pending
    // approvals are spoken in one short sentence each.
    const deliverAnnouncement = useCallback(
        (announcement: VoiceAnnouncement) => {
            const protocol = protocolRef.current;
            if (!protocol) {
                return;
            }

            if (
                deliveredAnnouncementKeysRef.current.has(announcement.dedupeKey)
            ) {
                return;
            }
            deliveredAnnouncementKeysRef.current.add(announcement.dedupeKey);

            const kind = announcement.kind;
            const isProgress = kind === 'run_progress';

            // The idle clock is held for the whole run, even when the
            // announcement itself has to wait.
            if (kind === 'run_started') {
                runHoldStartedAtRef.current = Date.now();
            }

            const inFlightTool = voiceToolCallInFlightRef.current;
            if (inFlightTool === 'send_current_thread_message') {
                // The send action is waiting for this run itself and reports
                // progress, the reply, an approval, or a failure in its result.
                return;
            }

            const responseActive =
                inFlightTool !== null ||
                (protocol === 'live'
                    ? liveResponseActiveRef.current
                    : realtimeResponseActiveRef.current);
            if (responseActive) {
                // Only one response can be active on either protocol. Progress
                // is stale by the time the response ends, so it is dropped and
                // a later activity may announce instead; everything else is
                // delivered from the response-completed handlers.
                deliveredAnnouncementKeysRef.current.delete(
                    announcement.dedupeKey
                );
                if (!isProgress) {
                    pendingAnnouncementsRef.current.push(announcement);
                }
                return;
            }

            if (kind !== 'run_started' && !isProgress) {
                runHoldStartedAtRef.current = null;
                markActivity();
            }

            const autoRead = widgetStateRef.current.autoReadEnabled;
            const isReply = kind === 'assistant_reply';
            const chunk =
                isReply && autoRead && announcement.text
                    ? sliceSpokenText(
                          announcement.text,
                          0,
                          autoReadChunkCharacters
                      )
                    : null;
            const continuation = chunk?.truncated
                ? ` That is the first ${chunk.nextOffset} characters. To continue, call read_current_thread_context with part last_assistant and offset ${chunk.nextOffset}.`
                : '';
            // GPT-Live's commentary.append is discretionary (the model "may
            // paraphrase" and decides whether to voice it), so the read-out
            // goes through the delegated model like every tool result does:
            // a context item plus response.create reliably produces speech.
            let contextText: string;
            if (chunk) {
                contextText = `[UI event] ${announcement.summary} Auto-read is on: read the following reply aloud to the user now, word for word, with no preamble or summary, then stop.${
                    chunk.truncated
                        ? ' After reading, say there is more and offer to continue.'
                        : ''
                }${continuation}

${chunk.text}`;
            } else if (isReply) {
                contextText = `[UI event] ${announcement.summary} It has not been read aloud. Tell the user in one short sentence that the reply is ready, mention any tools or files it names, and offer to read it.`;
            } else if (isProgress) {
                contextText = `[UI event] ${announcement.summary} Tell the user in one short sentence what the assistant is doing, then stop.`;
            } else if (kind === 'run_completed') {
                contextText = `[UI event] ${announcement.summary} Tell the user in one short sentence what the assistant did or produced, and offer to describe the outputs.`;
            } else {
                contextText = `[UI event] ${announcement.summary}`;
            }
            const shouldRespond = kind !== 'run_started';

            console.debug('Voice announcement', kind, {
                autoRead,
                spoken: !!chunk,
                protocol
            });

            if (protocol === 'live') {
                sendVoiceEvent({
                    type: 'response.item.create',
                    item: {
                        type: 'message',
                        role: 'user',
                        content: [{type: 'input_text', text: contextText}]
                    }
                });
                if (shouldRespond) {
                    sendVoiceEvent({type: 'response.create'});
                }
                return;
            }

            sendVoiceEvent({
                type: 'conversation.item.create',
                item: {
                    type: 'message',
                    role: 'system',
                    content: [{type: 'input_text', text: contextText}]
                }
            });
            if (chunk) {
                sendVoiceEvent({
                    type: 'response.create',
                    response: {
                        instructions:
                            'Read the reply in the latest system message aloud exactly as written, then stop.'
                    }
                });
            } else if (shouldRespond) {
                sendVoiceEvent({type: 'response.create'});
            }
        },
        [markActivity, sendVoiceEvent]
    );
    deliverAnnouncementRef.current = deliverAnnouncement;

    useEffect(
        () =>
            subscribeToAnnouncements(announcement => {
                if (connectionState !== 'connected') {
                    return;
                }
                deliverAnnouncementRef.current(announcement);
            }),
        [connectionState, subscribeToAnnouncements]
    );

    const speakIdleCheckIn = useCallback(() => {
        const seconds = Math.round(idleCheckInLeadMs / 1000);
        const text = `Still there? I will close the voice session in ${seconds} seconds unless you need anything.`;
        if (protocolRef.current === 'live') {
            sendVoiceEvent({
                type: 'response.item.create',
                item: {
                    type: 'message',
                    role: 'user',
                    content: [
                        {
                            type: 'input_text',
                            text: `[UI event] Nobody has spoken for a while. Say exactly: "${text}"`
                        }
                    ]
                }
            });
            sendVoiceEvent({type: 'response.create'});
            return;
        }
        if (!realtimeResponseActiveRef.current) {
            sendVoiceEvent({
                type: 'response.create',
                response: {instructions: `Say exactly: "${text}"`}
            });
        }
    }, [sendVoiceEvent]);

    const closeForIdle = useCallback(() => {
        const limit = idleTimeoutSecondsRef.current;
        disconnect(true, 'idle_closed');
        const message = `Voice session closed after ${limit} seconds of silence. Click the microphone to reconnect.`;
        setStatus(message);
        toast.info(message);
        playIdleCloseTone();
    }, [disconnect]);

    // One tick per second while connected: usage minutes, the idle check-in,
    // and the idle close.
    useEffect(() => {
        if (connectionState !== 'connected') {
            return;
        }

        connectedAtRef.current ??= Date.now();
        const interval = window.setInterval(() => {
            const now = Date.now();
            const connectedAt = connectedAtRef.current ?? now;
            const fullMinutes = Math.floor((now - connectedAt) / 60_000);
            while (minutesTrackedRef.current < fullMinutes) {
                minutesTrackedRef.current += 1;
                trackVoiceEvent('minute');
            }

            const limit = idleTimeoutSecondsRef.current;
            if (limit <= 0) {
                return;
            }

            const holdStartedAt = runHoldStartedAtRef.current;
            if (holdStartedAt !== null && now - holdStartedAt < runHoldCapMs) {
                return;
            }

            const remainingMs =
                limit * 1000 - (now - lastActivityAtRef.current);
            if (remainingMs <= 0) {
                closeForIdle();
                return;
            }

            if (remainingMs <= idleCheckInLeadMs) {
                setIdleSecondsRemaining(Math.ceil(remainingMs / 1000));
                if (!idleCheckInSentRef.current) {
                    idleCheckInSentRef.current = true;
                    speakIdleCheckIn();
                }
            }
        }, idleTickMs);

        return () => window.clearInterval(interval);
    }, [closeForIdle, connectionState, speakIdleCheckIn, trackVoiceEvent]);

    // Hand the microphone grant to the extension's full-tab permission page.
    // Not an error state: nothing failed, the user just has a step to do first.
    const requestMicrophonePermissionFromExtension = useCallback(() => {
        setConnectionState('idle');
        if (hasExtensionCapability(microphonePermissionCapability)) {
            postMicrophonePermissionRequest();
            setStatus(embeddedMicrophoneHelpMessage);
        } else {
            setStatus(embeddedMicrophoneUpdateMessage);
        }
    }, []);

    const connect = useCallback(async () => {
        setConnectionState('connecting');
        setErrorDetails(null);
        isMicrophoneMutedRef.current = false;
        setIsMicrophoneMuted(false);
        setStatus('Requesting microphone access...');

        if (embeddedInExtension && (await isMicrophonePermissionMissing())) {
            requestMicrophonePermissionFromExtension();
            return;
        }

        let requestingMicrophone = true;
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            requestingMicrophone = false;
            mediaStreamRef.current = mediaStream;

            const pageContext = getPageContext();

            const pc = new RTCPeerConnection();
            peerConnectionRef.current = pc;

            pc.ontrack = event => {
                const remoteAudio = remoteAudioRef.current;
                if (!remoteAudio) {
                    return;
                }

                remoteAudio.srcObject =
                    event.streams[0] ?? new MediaStream([event.track]);
                remoteAudio.muted = false;
                remoteAudio.volume = 1;
                remoteAudio.play().then(
                    () => setIsAudioPlaybackBlocked(false),
                    error => {
                        setIsAudioPlaybackBlocked(true);
                        setStatus(
                            'Audio playback was blocked. Select Play response.'
                        );
                        setErrorDetails(
                            createErrorDetails('remoteAudioPlayback', error)
                        );
                    }
                );
            };

            mediaStream
                .getAudioTracks()
                .forEach(track => pc.addTrack(track, mediaStream));

            const dataChannel = pc.createDataChannel('oai-events');
            dataChannelRef.current = dataChannel;
            dataChannel.addEventListener('open', () => {
                // A new provider session is a new conversation. Keep the
                // previous transcript available until this connection succeeds.
                setConversation([]);
                markActivity();
                if (protocolRef.current === 'realtime') {
                    updateAzureRealtimeContext(pageContext);
                    setConnectionState('connected');
                    setStatus('Listening.');
                    return;
                }

                setStatus('Starting GPT-Live session...');
            });
            dataChannel.addEventListener('message', event => {
                const protocol = protocolRef.current;
                const eventHandler =
                    protocol === 'realtime'
                        ? handleAzureRealtimeEvent
                        : handleLiveEvent;
                eventHandler(parseEvent(event)).catch(error => {
                    const message =
                        error instanceof Error
                            ? error.message
                            : `Unable to handle ${protocol ?? 'voice'} event.`;
                    setStatus(message);
                    setErrorDetails(
                        createErrorDetails(
                            protocol === 'realtime'
                                ? 'handleAzureRealtimeEvent'
                                : 'handleLiveEvent',
                            error
                        )
                    );
                });
            });
            dataChannel.addEventListener('close', () => {
                if (peerConnectionRef.current !== pc) {
                    return;
                }

                const error = `The ${
                    protocolRef.current === 'realtime'
                        ? 'Azure Realtime'
                        : 'GPT-Live'
                } connection closed unexpectedly.`;
                disconnect(false);
                setConnectionState('error');
                setStatus(error);
                setErrorDetails(
                    createResponseErrorDetails('voiceDataChannel', error)
                );
            });

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            await waitForIceGathering(pc);

            const sdp = pc.localDescription?.sdp;
            if (!sdp) {
                throw new Error('The browser did not create a WebRTC offer.');
            }

            setStatus('Creating voice session...');
            const sessionResponse: VoiceSessionResponse =
                await createVoiceSession({
                    sdp,
                    voice: selectedVoice,
                    instructions: buildVoiceActionInstructions(pageContext),
                    tools: buildVoiceTools(pageContext)
                });
            protocolRef.current = sessionResponse.protocol;

            if (sessionResponse.protocol === 'live') {
                await pc.setRemoteDescription({
                    type: 'answer',
                    sdp: sessionResponse.answerSdp
                });
                return;
            }

            setStatus('Connecting to Azure Realtime...');
            const answerResponse = await fetch(
                sessionResponse.realtimeCallsUrl,
                {
                    method: 'POST',
                    body: sdp,
                    headers: {
                        Authorization: `Bearer ${sessionResponse.clientSecret}`,
                        'Content-Type': 'application/sdp'
                    }
                }
            );
            if (!answerResponse.ok) {
                const responseBody = await answerResponse.text();
                throw new Error(
                    `Azure Realtime WebRTC connection failed (${answerResponse.status} ${answerResponse.statusText}).${
                        responseBody
                            ? ` Provider response: ${responseBody.slice(0, 16_384)}`
                            : ''
                    }`
                );
            }

            await pc.setRemoteDescription({
                type: 'answer',
                sdp: await answerResponse.text()
            });
        } catch (error) {
            disconnect();
            if (embeddedInExtension && isMicrophonePermissionError(error)) {
                // The permission check above can be unavailable or stale;
                // the refusal itself is the reliable signal.
                requestMicrophonePermissionFromExtension();
                setErrorDetails(createErrorDetails('connect', error));
                return;
            }
            setConnectionState('error');
            console.error('Voice assistant connection failed', error);
            if (!requestingMicrophone) {
                setStatus(getConnectErrorMessage(error));
                setErrorDetails(createErrorDetails('connect', error));
                return;
            }

            const microphoneFailure = await classifyMicrophoneFailure(error);
            setStatus(
                isRunningInTeams && isMicrophonePermissionError(error)
                    ? teamsMicrophoneBlockedMessage
                    : getMicrophoneFailureMessage(microphoneFailure, error)
            );
            setErrorDetails({
                ...createErrorDetails('microphone', error),
                microphoneFailure
            });
        }
    }, [
        createVoiceSession,
        disconnect,
        getPageContext,
        handleAzureRealtimeEvent,
        handleLiveEvent,
        isRunningInTeams,
        markActivity,
        requestMicrophonePermissionFromExtension,
        selectedVoice,
        updateAzureRealtimeContext
    ]);

    useEffect(() => {
        if (connectionState === 'connected') {
            const pageContext = getPageContext();
            if (protocolRef.current === 'realtime') {
                updateAzureRealtimeContext(pageContext);
            } else {
                updateLiveContext(pageContext);
            }
        }
    }, [
        actionVersion,
        connectionState,
        getPageContext,
        updateAzureRealtimeContext,
        updateLiveContext
    ]);

    // Inside the Chrome extension, tell the side panel when a voice session is
    // open so it pushes the active tab's title/URL (and stops when it closes).
    // Covers both protocols' connect paths, Disconnect, idle close, and unmount.
    useEffect(() => {
        if (!embeddedInExtension) {
            return;
        }
        postVoiceSessionState(connectionState === 'connected');
        return () => postVoiceSessionState(false);
    }, [connectionState]);

    useEffect(
        () => () => {
            if (tooltipHideTimeoutRef.current !== null) {
                window.clearTimeout(tooltipHideTimeoutRef.current);
            }
            if (copyFeedbackTimeoutRef.current !== null) {
                window.clearTimeout(copyFeedbackTimeoutRef.current);
            }

            disconnect();
        },
        [disconnect]
    );

    const updateLeftTooltipPosition = useCallback(() => {
        if (tooltipPlacement !== 'left') {
            return;
        }

        const controlElement = controlRef.current;
        if (!controlElement) {
            return;
        }

        const controlRect = controlElement.getBoundingClientRect();
        const measuredHeight = tooltipRef.current?.offsetHeight;
        const fallbackHeight = Math.min(window.innerHeight * 0.7, 448);
        const tooltipHeight = measuredHeight || fallbackHeight;
        const centeredTop =
            controlRect.top + controlRect.height / 2 - tooltipHeight / 2;
        const maxTop = Math.max(
            tooltipViewportMarginPx,
            window.innerHeight - tooltipHeight - tooltipViewportMarginPx
        );
        const viewportTop = Math.min(
            maxTop,
            Math.max(tooltipViewportMarginPx, centeredTop)
        );

        setLeftTooltipTop(viewportTop - controlRect.top);
    }, [tooltipPlacement]);

    useLayoutEffect(() => {
        if (!isTooltipOpen || tooltipPlacement !== 'left') {
            return;
        }

        const animationFrame = window.requestAnimationFrame(
            updateLeftTooltipPosition
        );

        return () => window.cancelAnimationFrame(animationFrame);
    }, [
        conversation,
        errorDetails,
        isTooltipOpen,
        status,
        tooltipPlacement,
        updateLeftTooltipPosition
    ]);

    useEffect(() => {
        if (!isTooltipOpen || tooltipPlacement !== 'left') {
            return;
        }

        window.addEventListener('resize', updateLeftTooltipPosition);
        window.addEventListener('scroll', updateLeftTooltipPosition, true);

        return () => {
            window.removeEventListener('resize', updateLeftTooltipPosition);
            window.removeEventListener(
                'scroll',
                updateLeftTooltipPosition,
                true
            );
        };
    }, [isTooltipOpen, tooltipPlacement, updateLeftTooltipPosition]);

    const startConnect = () => {
        connect().catch(error => {
            setConnectionState('error');
            setStatus(
                error instanceof Error ? error.message : 'Unable to connect.'
            );
            setErrorDetails(createErrorDetails('connectClick', error));
        });
    };

    // Desktop: the tab itself connects or mutes. Touch: the tab only opens the
    // sheet, and the sheet's large button connects, so an accidental tap on
    // the screen edge never starts a billed session.
    const handleClick = () => {
        if (isSheet) {
            setIsSheetOpen(true);
            return;
        }
        if (connectionState === 'connected') {
            setMicrophoneMuted(!isMicrophoneMuted);
            return;
        }
        startConnect();
    };

    const playResponse = async () => {
        try {
            await remoteAudioRef.current?.play();
            setIsAudioPlaybackBlocked(false);
            setStatus('Listening.');
            setErrorDetails(current =>
                current?.stage === 'remoteAudioPlayback' ? null : current
            );
        } catch (error) {
            setStatus('Unable to play the assistant audio.');
            setErrorDetails(createErrorDetails('remoteAudioPlayback', error));
        }
    };

    const downloadTranscript = (format: VoiceTranscriptFormat) => {
        downloadVoiceTranscript({
            lines: conversation,
            format,
            voiceLabel: getRealtimeVoiceLabel(selectedVoice)
        });
    };

    const stateColor =
        connectionState === 'connecting'
            ? 'var(--primary-color)'
            : connectionState === 'connected'
              ? isMicrophoneMuted
                  ? '#ffc107'
                  : '#198754'
              : connectionState === 'error'
                ? '#dc3545'
                : undefined;

    // Everything the panel shows, shared by the desktop popover and the phone
    // sheet so the two layouts cannot drift apart.
    const panelProps: Omit<VoicePanelContentProps, 'layout' | 'onClose'> = {
        idPrefix: id,
        status,
        activity,
        connectionState,
        stateColor,
        isMuted: isMicrophoneMuted,
        idleSecondsRemaining,
        autoReadEnabled,
        selectedVoiceLabel: getRealtimeVoiceLabel(selectedVoice),
        conversation,
        errorDetails,
        errorCopied,
        isAudioPlaybackBlocked,
        providerNote,
        onConnect: startConnect,
        onToggleMute: () => setMicrophoneMuted(!isMicrophoneMuted),
        onDisconnect: () => disconnect(),
        onPlayResponse: () => {
            void playResponse();
        },
        onOpenVoiceSettings: () => {
            setIsVoiceSettingsOpen(true);
            setIsTooltipOpen(false);
        },
        onSetAutoRead: setAutoReadEnabled,
        onDownloadTranscript: downloadTranscript,
        onCopyError: () => {
            void copyErrorDetails();
        }
    };

    // One icon per state: outline when there is no session, filled while
    // listening, struck through only when muted, so mute and disconnected are
    // never confused.
    const icon =
        connectionState === 'connecting' ? (
            <ImSpinner2 className="voice-assistant-control__spinner" />
        ) : connectionState === 'connected' ? (
            isMicrophoneMuted ? (
                <MdMicOff />
            ) : (
                <MdMic />
            )
        ) : (
            <MdMicNone />
        );
    const tooltipId = `${id}-tooltip`;
    const buttonTitle =
        connectionState === 'connecting'
            ? 'Connecting voice assistant'
            : connectionState === 'connected'
              ? isMicrophoneMuted
                  ? 'Unmute microphone'
                  : 'Mute microphone'
              : connectionState === 'error'
                ? 'Voice assistant error. Click to reconnect'
                : 'Connect voice assistant';
    // The hover title reports the work in progress; the accessible name keeps
    // naming what the button does, which is still mute or connect.
    const buttonHoverTitle = activity ? activity.label : buttonTitle;
    const showTooltip = () => {
        if (tooltipHideTimeoutRef.current !== null) {
            window.clearTimeout(tooltipHideTimeoutRef.current);
            tooltipHideTimeoutRef.current = null;
        }

        setIsTooltipOpen(true);
    };
    const hideTooltip = () => {
        if (tooltipHideTimeoutRef.current !== null) {
            window.clearTimeout(tooltipHideTimeoutRef.current);
        }

        tooltipHideTimeoutRef.current = window.setTimeout(() => {
            tooltipHideTimeoutRef.current = null;
            setIsTooltipOpen(false);
        }, tooltipHideDelayMs);
    };
    const tooltipStyle: React.CSSProperties = {
        position: 'absolute',
        width: 'min(22rem, calc(100vw - 1rem))',
        maxHeight: 'min(70vh, 28rem)',
        overflowY: 'auto',
        padding: '0.75rem',
        borderRadius: '0.5rem',
        border: '1px solid var(--input-border-static)',
        background: 'var(--theme-70)',
        color: 'var(--body-color)',
        fontSize: '0.875rem',
        lineHeight: 1.35,
        zIndex: 1031,
        ...(tooltipPlacement === 'left'
            ? {
                  top: leftTooltipTop ?? '50%',
                  right: `calc(100% + ${tooltipGapPx}px)`,
                  transform:
                      leftTooltipTop === null ? 'translateY(-50%)' : undefined
              }
            : {
                  top: 'calc(100% + 0.5rem)',
                  right: 0
              })
    };

    const sheetTitle = 'Voice assistant controls';

    return (
        <div
            ref={controlRef}
            className="voice-assistant-control position-relative d-inline-flex"
            onMouseEnter={isSheet ? undefined : showTooltip}
            onMouseLeave={isSheet ? undefined : hideTooltip}
        >
            <IconButton
                id={id}
                className={className}
                icon={icon}
                onClick={handleClick}
                onFocus={isSheet ? undefined : showTooltip}
                onBlur={isSheet ? undefined : hideTooltip}
                disabled={!isSheet && connectionState === 'connecting'}
                title={isSheet ? sheetTitle : buttonHoverTitle}
                aria-label={isSheet ? sheetTitle : buttonTitle}
                aria-expanded={isSheet ? isSheetOpen : undefined}
                aria-describedby={
                    !isSheet && isTooltipOpen ? tooltipId : undefined
                }
                style={{color: stateColor}}
            />
            {activity && (
                <span
                    className="voice-assistant-control__activity"
                    title={activity.label}
                    aria-hidden="true"
                >
                    <ImSpinner2 className="voice-assistant-control__spinner" />
                </span>
            )}
            <span role="status" className="visually-hidden">
                {activity?.label ?? ''}
            </span>
            {!isSheet && isTooltipOpen && (
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    className="shadow"
                    style={tooltipStyle}
                    onMouseEnter={showTooltip}
                    onMouseLeave={hideTooltip}
                >
                    <VoicePanelContent layout="compact" {...panelProps} />
                </div>
            )}
            {isSheet && (
                // Reactstrap portals the sheet to <body>, but React events
                // still bubble through this tree, so stop pointer events here
                // or the dock's drag handler would see every tap in the sheet.
                <div
                    onPointerDown={event => event.stopPropagation()}
                    onClick={event => event.stopPropagation()}
                >
                    <Offcanvas
                        direction="bottom"
                        isOpen={isSheetOpen}
                        toggle={() => setIsSheetOpen(false)}
                        backdrop
                        scrollable
                        fade
                        className="voice-sheet"
                        aria-labelledby={`${id}-sheet-title`}
                    >
                        <OffcanvasBody className="voice-sheet__body">
                            <VoicePanelContent
                                layout="touch"
                                onClose={() => setIsSheetOpen(false)}
                                {...panelProps}
                            />
                        </OffcanvasBody>
                    </Offcanvas>
                </div>
            )}
            <VoiceSettingsModal
                id={id}
                isOpen={isVoiceSettingsOpen}
                isConnected={connectionState === 'connected'}
                selectedVoice={selectedVoice}
                setSelectedVoice={setSelectedVoice}
                onClose={() => setIsVoiceSettingsOpen(false)}
            />
            <audio ref={remoteAudioRef} autoPlay playsInline />
        </div>
    );
};

export default VoiceAssistantControl;
